import React, { useState, useMemo, useRef, useEffect } from 'react';

export interface DtColumn<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number | null;
  filterValue?: (row: T) => string;
  exportValue?: (row: T) => string;
  noExport?: boolean;
  width?: string;
}

interface Props<T> {
  columns: DtColumn<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  onDeleteSelected?: (rows: T[]) => void;
  defaultPageSize?: number;
  exportFilename?: string;
  searchPlaceholder?: string;
}

const PAGE_SIZES = [25, 50, 100, 250];

const pb = (disabled: boolean, active = false): React.CSSProperties => ({
  padding: '4px 9px',
  border: '1px solid #ddd',
  borderRadius: 4,
  fontSize: 12,
  cursor: disabled ? 'default' : 'pointer',
  background: active ? '#1a5276' : disabled ? '#f8f8f8' : 'white',
  color: active ? 'white' : disabled ? '#bbb' : '#333',
  fontWeight: active ? 600 : 400,
});

export default function DataTable<T,>({
  columns,
  data,
  rowKey,
  onDeleteSelected,
  defaultPageSize = 25,
  exportFilename = 'export',
  searchPlaceholder = 'Ara...',
}: Props<T>) {
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  const [filterOpen, setFilterOpen] = useState<{ key: string; top: number; left: number } | null>(null);
  const [filterQ, setFilterQ] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);
  const ddRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const h = (e: MouseEvent) => {
      if (!ddRef.current?.contains(e.target as Node)) setFilterOpen(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [filterOpen]);

  useEffect(() => { setPage(1); }, [search, colFilters, data]);
  useEffect(() => { setSelected(new Set()); }, [data]);

  const afterSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(row =>
      columns.some(col => {
        const v = col.filterValue
          ? col.filterValue(row)
          : col.render ? '' : String((row as any)[col.key] ?? '');
        return v.toLowerCase().includes(q);
      })
    );
  }, [data, search, columns]);

  const filtered = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => v.size > 0);
    if (!active.length) return afterSearch;
    return afterSearch.filter(row =>
      active.every(([k, vals]) => {
        const col = columns.find(c => c.key === k);
        const v = col?.filterValue ? col.filterValue(row) : String((row as any)[k] ?? '');
        return vals.has(v);
      })
    );
  }, [afterSearch, colFilters, columns]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    const col = columns.find(c => c.key === sortCol);
    return [...filtered].sort((a, b) => {
      const av = col?.sortValue ? col.sortValue(a) : String((a as any)[sortCol] ?? '');
      const bv = col?.sortValue ? col.sortValue(b) : String((b as any)[sortCol] ?? '');
      if (av === null || av === '') return 1;
      if (bv === null || bv === '') return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'tr');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cp = Math.min(page, totalPages);
  const pageRows = sorted.slice((cp - 1) * pageSize, cp * pageSize);

  const getUniq = (colKey: string) => {
    const col = columns.find(c => c.key === colKey);
    const vals = afterSearch.map(r =>
      col?.filterValue ? col.filterValue(r) : String((r as any)[colKey] ?? '')
    );
    return Array.from(new Set(vals)).sort((a, b) => a.localeCompare(b, 'tr'));
  };

  const allSel = pageRows.length > 0 && pageRows.every(r => selected.has(rowKey(r)));
  const selRows = sorted.filter(r => selected.has(rowKey(r)));
  const activeFCount = Object.values(colFilters).filter(s => s.size > 0).length;

  const handleSort = (key: string) => {
    if (sortCol === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(key); setSortDir('asc'); }
  };

  const openFilter = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setFilterOpen(prev => (prev?.key === key ? null : { key, top: r.bottom + 2, left: r.left }));
    setFilterQ('');
  };

  const toggleFVal = (colKey: string, val: string) => {
    setColFilters(prev => {
      const s = new Set(prev[colKey] ?? []);
      s.has(val) ? s.delete(val) : s.add(val);
      return { ...prev, [colKey]: s };
    });
  };

  const clearF = (colKey: string) =>
    setColFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });

  const exportCSV = () => {
    const rows = selRows.length > 0 ? selRows : sorted;
    const cols = columns.filter(c => !c.noExport);
    const bom = '﻿';
    const hdr = cols.map(c => `"${c.label}"`).join(';');
    const body = rows
      .map(r =>
        cols
          .map(c => {
            const v = c.exportValue
              ? c.exportValue(r)
              : c.filterValue
              ? c.filterValue(r)
              : String((r as any)[c.key] ?? '');
            return `"${v.replace(/"/g, '""')}"`;
          })
          .join(';')
      )
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob([bom + hdr + '\n' + body], { type: 'text/csv;charset=utf-8;' })
    );
    a.download = `${exportFilename}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const pageNums = (() => {
    const s = Math.max(1, Math.min(cp - 2, totalPages - 4));
    return Array.from({ length: Math.min(5, totalPages - s + 1) }, (_, i) => s + i);
  })();

  const fVals = filterOpen
    ? getUniq(filterOpen.key).filter(
        v => !filterQ || v.toLowerCase().includes(filterQ.toLowerCase())
      )
    : [];
  const fSet = filterOpen
    ? colFilters[filterOpen.key] ?? new Set<string>()
    : new Set<string>();

  return (
    <>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="search-input"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          {activeFCount > 0 && (
            <button
              className="btn btn-sm"
              style={{
                background: '#e8f4fd',
                color: '#1a5276',
                border: '1px solid #aed6f1',
                padding: '4px 10px',
                fontSize: 12,
              }}
              onClick={() => setColFilters({})}
            >
              ✕ Sütun filtreleri ({activeFCount})
            </button>
          )}
          {selected.size > 0 && (
            <span
              style={{
                fontSize: 12,
                color: '#1a5276',
                background: '#eaf2fb',
                padding: '4px 10px',
                borderRadius: 4,
              }}
            >
              {selected.size} kayıt seçili
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            className="search-input"
            style={{ width: 80, padding: '6px 8px' }}
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {PAGE_SIZES.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            className="btn btn-sm"
            style={{ background: '#27ae60', color: 'white', padding: '5px 12px' }}
            onClick={exportCSV}
          >
            ⬇ CSV {selRows.length > 0 ? `(${selRows.length} seçili)` : ''}
          </button>
          {onDeleteSelected && selRows.length > 0 && (
            <button
              className="btn btn-sm btn-danger"
              style={{ padding: '5px 12px' }}
              onClick={() => {
                if (window.confirm(`${selRows.length} kayıt silinecek. Emin misiniz?`)) {
                  onDeleteSelected(selRows);
                  setSelected(new Set());
                }
              }}
            >
              🗑 Sil ({selRows.length})
            </button>
          )}
        </div>
      </div>

      {/* Tablo */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center', padding: '9px 6px' }}>
                <input
                  type="checkbox"
                  checked={allSel}
                  onChange={() =>
                    allSel
                      ? setSelected(new Set())
                      : setSelected(new Set(pageRows.map(r => rowKey(r))))
                  }
                  style={{ cursor: 'pointer', width: 14, height: 14 }}
                />
              </th>
              {columns.map(col => {
                const hf = (colFilters[col.key]?.size ?? 0) > 0;
                return (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && handleSort(col.key)}
                    style={{
                      width: col.width,
                      cursor: col.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      background: hf ? '#154360' : undefined,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ flex: 1 }}>{col.label}</span>
                      {col.sortable && (
                        <span style={{ fontSize: 10, opacity: sortCol === col.key ? 1 : 0.5 }}>
                          {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      )}
                      {col.filterable && (
                        <button
                          onClick={e => openFilter(e, col.key)}
                          style={{
                            background: hf ? '#f0b429' : 'rgba(255,255,255,0.25)',
                            border: 'none',
                            borderRadius: 3,
                            color: hf ? '#1a3a52' : 'white',
                            padding: '1px 5px',
                            fontSize: 10,
                            cursor: 'pointer',
                            lineHeight: '16px',
                          }}
                        >
                          {hf ? '●' : '▾'}
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map(row => {
              const k = rowKey(row);
              return (
                <tr key={k} style={selected.has(k) ? { background: '#eaf2fb' } : {}}>
                  <td style={{ textAlign: 'center', padding: '8px 6px' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(k)}
                      onChange={() =>
                        setSelected(prev => {
                          const ns = new Set(prev);
                          ns.has(k) ? ns.delete(k) : ns.add(k);
                          return ns;
                        })
                      }
                      style={{ cursor: 'pointer', width: 14, height: 14 }}
                    />
                  </td>
                  {columns.map(col => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : String((row as any)[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  style={{ textAlign: 'center', color: '#999', padding: 20 }}
                >
                  Kayıt bulunamadı
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sayfalama */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 10,
          fontSize: 12,
          color: '#666',
        }}
      >
        <span>
          {sorted.length > 0
            ? `${(cp - 1) * pageSize + 1}–${Math.min(cp * pageSize, sorted.length)} / ${sorted.length} kayıt`
            : '0 kayıt'}
          {data.length !== sorted.length && ` (toplam ${data.length})`}
        </span>
        <div style={{ display: 'flex', gap: 3 }}>
          <button disabled={cp === 1} onClick={() => setPage(1)} style={pb(cp === 1)}>
            «
          </button>
          <button disabled={cp === 1} onClick={() => setPage(p => p - 1)} style={pb(cp === 1)}>
            ‹
          </button>
          {pageNums.map(n => (
            <button key={n} onClick={() => setPage(n)} style={pb(false, n === cp)}>
              {n}
            </button>
          ))}
          <button
            disabled={cp === totalPages}
            onClick={() => setPage(p => p + 1)}
            style={pb(cp === totalPages)}
          >
            ›
          </button>
          <button
            disabled={cp === totalPages}
            onClick={() => setPage(totalPages)}
            style={pb(cp === totalPages)}
          >
            »
          </button>
        </div>
      </div>

      {/* Sütun filtre dropdown (fixed konumlu) */}
      {filterOpen && (
        <div
          ref={ddRef}
          style={{
            position: 'fixed',
            top: filterOpen.top,
            left: filterOpen.left,
            zIndex: 9999,
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: 6,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            minWidth: 220,
            maxWidth: 300,
            padding: 10,
            color: '#333',
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1a5276', marginBottom: 6 }}>
            {columns.find(c => c.key === filterOpen.key)?.label} — Filtre
          </div>
          <input
            type="text"
            placeholder="Değer ara..."
            value={filterQ}
            onChange={e => setFilterQ(e.target.value)}
            autoFocus
            style={{
              width: '100%',
              padding: '5px 8px',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 12,
              marginBottom: 6,
            }}
          />
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
            {fVals.length === 0 ? (
              <div style={{ color: '#999', fontSize: 12, textAlign: 'center', padding: 8 }}>
                Değer bulunamadı
              </div>
            ) : (
              fVals.map(v => (
                <label
                  key={v}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '4px',
                    cursor: 'pointer',
                    fontSize: 12,
                    borderRadius: 3,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f0f4f8')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <input
                    type="checkbox"
                    checked={fSet.has(v)}
                    onChange={() => toggleFVal(filterOpen.key, v)}
                  />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {v || '(boş)'}
                  </span>
                </label>
              ))
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #eee', paddingTop: 8 }}>
            <button
              onClick={() => clearF(filterOpen.key)}
              style={{
                flex: 1,
                padding: '5px',
                background: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Temizle
            </button>
            <button
              onClick={() => setFilterOpen(null)}
              style={{
                flex: 1,
                padding: '5px',
                background: '#1a5276',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 'bold',
              }}
            >
              Uygula ✓
            </button>
          </div>
        </div>
      )}
    </>
  );
}
